import React from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faTrophy, faPieChart, faWallet, faUsers, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const DonationAnalytics = ({ analyticsData }) => {
    const { revenueOverTime = [], statusDistribution = [], topCampaigns = [], methodDistribution = [], geographicSpread = [], donorRetention = [] } = analyticsData || {};



    const formatShortCurrency = (value) => {
        if (value >= 1000000) return `EGP ${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `EGP ${(value / 1000).toFixed(1)}k`;
        return `EGP ${value}`;
    };

    if (!analyticsData) return null;

    return (
        <div className="analytics-dashboard mb-4">
            <h4 className="mb-4 text-secondary">
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Donation Analytics
            </h4>
            
            <div className="row g-4">
                {/* Donations Over Time Line Chart */}
                <div className="col-12 col-xl-8">
                    <div className="card shadow-sm h-100 border-0">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h5 className="card-title fw-bold">
                                <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
                                Donations Over Time (Last 30 Days)
                            </h5>
                        </div>
                        <div className="card-body" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={revenueOverTime} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                    <XAxis dataKey="date" tick={{fontSize: 12}} tickMargin={10} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="left" tick={{fontSize: 12}} tickFormatter={(value) => `EGP ${value}`} axisLine={false} tickLine={false} />
                                    <Tooltip formatter={(value) => `EGP ${value}`} />
                                    <Legend />
                                    <Line yAxisId="left" type="monotone" dataKey="amount" name="Total Donations" stroke="#0d6efd" strokeWidth={3} dot={{r: 4}} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Top Campaigns Bar Chart */}
                <div className="col-12 col-xl-4">
                    <div className="card shadow-sm h-100 border-0">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h5 className="card-title fw-bold">
                                <FontAwesomeIcon icon={faTrophy} className="me-2 text-warning" />
                                Top 5 Campaigns
                            </h5>
                        </div>
                        <div className="card-body" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={topCampaigns} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eee" />
                                    <XAxis type="number" tick={{fontSize: 12}} tickFormatter={formatShortCurrency} />
                                    <YAxis dataKey="name" type="category" width={110} tick={{fontSize: 11}} axisLine={false} tickLine={false} />
                                    <Tooltip formatter={(value) => [`EGP ${value}`, 'Raised']} />
                                    <Bar dataKey="value" fill="#ffc107" radius={[0, 4, 4, 0]} barSize={20} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Payment Methods and Status Pie Charts */}
                <div className="col-12 col-lg-4">
                    <div className="card shadow-sm h-100 border-0">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h5 className="card-title fw-bold">
                                <FontAwesomeIcon icon={faWallet} className="me-2 text-success" />
                                Donations by Gateway
                            </h5>
                        </div>
                        <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={methodDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={90} fill="#8884d8" paddingAngle={5} dataKey="value" label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                                        {methodDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`EGP ${value}`, 'Donations']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-4">
                    <div className="card shadow-sm h-100 border-0">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h5 className="card-title fw-bold">
                                <FontAwesomeIcon icon={faPieChart} className="me-2 text-info" />
                                Status Distribution
                            </h5>
                        </div>
                        <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={statusDistribution} cx="50%" cy="50%" innerRadius={0} outerRadius={90} fill="#8884d8" paddingAngle={2} dataKey="value" label={({name, value}) => `${name}: ${value}`}>
                                        {statusDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [value, 'Count']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Donor Retention */}
                <div className="col-12 col-lg-4">
                    <div className="card shadow-sm h-100 border-0">
                        <div className="card-header bg-white border-0 pt-4 pb-0">
                            <h5 className="card-title fw-bold">
                                <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                                Donor Retention
                            </h5>
                        </div>
                        <div className="card-body d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={donorRetention} cx="50%" cy="50%" innerRadius={60} outerRadius={90} fill="#8884d8" paddingAngle={5} dataKey="value" label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                                        {donorRetention.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={['#0d6efd', '#20c997'][index % 2]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`EGP ${value}`, 'Donations']} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>



            </div>
        </div>
    );
};

export default DonationAnalytics;
